/**
 * Temporal API utilities for SmartTimeInput
 * Helper functions for working with Temporal.PlainTime and Temporal.ZonedDateTime
 */

import { Temporal } from '@js-temporal/polyfill'

import {
  formatPlainTemporal,
  getDefaultTimeZone,
} from '../../../helpers/timeZone'

/**
 * Converts a time object to Temporal.PlainTime
 */
export function createPlainTime(
  hour: number,
  minute: number,
): Temporal.PlainTime {
  return new Temporal.PlainTime(hour, minute)
}

/**
 * Extracts PlainTime from various Temporal types
 */
export function extractPlainTime(
  value: Temporal.PlainTime | Temporal.ZonedDateTime,
): Temporal.PlainTime {
  if (value instanceof Temporal.PlainTime) {
    return value
  }
  if (value instanceof Temporal.ZonedDateTime) {
    return value.toPlainTime()
  }
  throw new Error('Unsupported temporal type')
}

/**
 * Creates a ZonedDateTime from PlainTime and timezone
 */
export function createZonedDateTime(
  plainTime: Temporal.PlainTime,
  timezone: string,
  date?: Temporal.PlainDate,
): Temporal.ZonedDateTime {
  const targetDate = date || Temporal.Now.plainDateISO(timezone)
  const plainDateTime = targetDate.toPlainDateTime(plainTime)
  return plainDateTime.toZonedDateTime(timezone)
}

/**
 * Formats time for display in en-US (pinned for parser round-trip)
 */
export function formatTime(
  time: Temporal.PlainTime,
  format: '12' | '24' = '12',
): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: format === '24' ? '2-digit' : 'numeric',
    minute: '2-digit',
  }

  if (format === '24') {
    options.hour12 = false
    options.hourCycle = 'h23'
  } else {
    options.hour12 = true
  }

  return formatPlainTemporal(time, 'en-US', options)
}

/**
 * Compares two PlainTime objects
 */
export function compareTime(
  a: Temporal.PlainTime,
  b: Temporal.PlainTime,
): number {
  return Temporal.PlainTime.compare(a, b)
}

/**
 * Checks if a time is within a range (inclusive)
 */
export function isTimeInRange(
  time: Temporal.PlainTime,
  min?: Temporal.PlainTime,
  max?: Temporal.PlainTime,
): boolean {
  if (!min && !max) {
    return true
  }

  if (min && max) {
    const wraps = compareTime(min, max) > 0
    if (wraps) {
      return compareTime(time, min) >= 0 || compareTime(time, max) <= 0
    }
    return compareTime(time, min) >= 0 && compareTime(time, max) <= 0
  }

  if (min) {
    return compareTime(time, min) >= 0
  }

  if (max) {
    return compareTime(time, max) <= 0
  }

  return true
}

/**
 * Clamps a time to a range
 */
export function clampTime(
  time: Temporal.PlainTime,
  min?: Temporal.PlainTime,
  max?: Temporal.PlainTime,
): Temporal.PlainTime {
  if (!min && !max) {
    return time
  }

  if (min && max) {
    const wraps = compareTime(min, max) > 0
    if (wraps) {
      if (compareTime(time, min) >= 0 || compareTime(time, max) <= 0) {
        return time
      }

      const minutesInDay = 24 * 60
      const toTotalMinutes = (value: Temporal.PlainTime) =>
        value.hour * 60 + value.minute

      const normalizeDistance = (
        from: Temporal.PlainTime,
        to: Temporal.PlainTime,
      ) => {
        const diff = Math.abs(toTotalMinutes(to) - toTotalMinutes(from))
        return Math.min(diff, minutesInDay - diff)
      }

      const distanceToMin = normalizeDistance(time, min)
      const distanceToMax = normalizeDistance(time, max)
      return distanceToMin <= distanceToMax ? min : max
    }

    if (compareTime(time, min) < 0) {
      return min
    }

    if (compareTime(time, max) > 0) {
      return max
    }

    return time
  }

  if (min && compareTime(time, min) < 0) {
    return min
  }

  if (max && compareTime(time, max) > 0) {
    return max
  }

  return time
}

/**
 * Gets the current time in a specific timezone
 */
export function getCurrentTime(timezone?: string): Temporal.PlainTime {
  if (timezone) {
    return Temporal.Now.zonedDateTimeISO(timezone).toPlainTime()
  }
  return Temporal.Now.plainTimeISO(getDefaultTimeZone())
}

/**
 * Adds minutes to a time
 */
export function addMinutes(
  time: Temporal.PlainTime,
  minutes: number,
): Temporal.PlainTime {
  const minutesPerDay = 24 * 60
  let totalMinutes = (time.hour * 60 + time.minute + minutes) % minutesPerDay
  if (totalMinutes < 0) {
    totalMinutes += minutesPerDay
  }
  const hour = Math.floor(totalMinutes / 60)
  const minute = totalMinutes % 60

  return createPlainTime(hour, minute)
}

/**
 * Gets the difference in minutes between two times
 */
export function getMinutesDifference(
  a: Temporal.PlainTime,
  b: Temporal.PlainTime,
): number {
  const aMinutes = a.hour * 60 + a.minute
  const bMinutes = b.hour * 60 + b.minute
  return bMinutes - aMinutes
}

/**
 * Rounds time to nearest step interval
 */
export function roundTimeToStep(
  time: Temporal.PlainTime,
  step: number,
): Temporal.PlainTime {
  const minutesPerDay = 24 * 60
  const totalMinutes = time.hour * 60 + time.minute
  let roundedMinutes = Math.round(totalMinutes / step) * step
  roundedMinutes =
    ((roundedMinutes % minutesPerDay) + minutesPerDay) % minutesPerDay
  const hour = Math.floor(roundedMinutes / 60)
  const minute = roundedMinutes % 60

  return createPlainTime(hour, minute)
}

/**
 * Checks if two times are equal
 */
export function isTimeEqual(
  a: Temporal.PlainTime,
  b: Temporal.PlainTime,
): boolean {
  return compareTime(a, b) === 0
}

/**
 * Validates timezone string using IANA format only.
 * Rejects fixed-offset abbreviations like EST, PST which don't observe DST.
 *
 * This is the single source of truth for timezone validation in SmartTimeInput.
 * All timezone validation should go through this function to ensure consistency.
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    // Defer to the Intl time-zone resolver for validation. Temporal dropped the
    // standalone Temporal.TimeZone object in @js-temporal/polyfill v0.5, so we
    // validate the same way the engine (and Temporal itself) does internally.
    // This accepts all valid IANA timezone identifiers including:
    // - Multi-level paths: America/Argentina/Buenos_Aires
    // - Zones with digits/hyphens: America/Indiana/Knox_IN-46534
    // - UTC and other special zones
    // Intl.DateTimeFormat throws a RangeError for invalid formats.
    new Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

/**
 * Creates time suggestions based on common intervals
 */
export function generateSmartSuggestions(
  input: string,
  currentTime?: Temporal.PlainTime,
  step: number = 30,
): Temporal.PlainTime[] {
  const suggestions: Temporal.PlainTime[] = []
  const now = currentTime || getCurrentTime()

  // Common times
  const commonTimes = [
    createPlainTime(9, 0), // 9 AM
    createPlainTime(12, 0), // Noon
    createPlainTime(13, 0), // 1 PM
    createPlainTime(17, 0), // 5 PM
    createPlainTime(18, 0), // 6 PM
  ]

  // Add common times that don't conflict with current time
  commonTimes.forEach((time) => {
    if (!isTimeEqual(time, now)) {
      suggestions.push(time)
    }
  })

  // Add times around current time
  const nearbyTimes = [
    roundTimeToStep(addMinutes(now, step), step),
    roundTimeToStep(addMinutes(now, step * 2), step),
    roundTimeToStep(addMinutes(now, step * 4), step),
  ]

  nearbyTimes.forEach((time) => {
    if (!suggestions.some((existing) => isTimeEqual(existing, time))) {
      suggestions.push(time)
    }
  })

  return suggestions.slice(0, 8) // Limit to 8 suggestions
}
